// Rage detection utilities
import { DEFAULTS, getISTTime, toISTTime } from "../constants.js";

export class RageDetectionManager {
  constructor() {
    this.rageEventTracker = {
      events: new Map(),
      lastCleanup: Date.now(),
      cleanupInterval: 3000,
      pendingRageEvents: new Map(),
      rageTimeouts: new Map(),
    };
    this.cleanupTimer = setInterval(() => this.cleanupRageEventTracker(), 30000);
  }

  configureRageDetection(config = {}) {
    const defaults = {
      rageClickThreshold: DEFAULTS.RAGE_CLICK_THRESHOLD,
      rageClickWindow: DEFAULTS.RAGE_CLICK_WINDOW,
      rageEventThreshold: DEFAULTS.RAGE_EVENT_THRESHOLD,
      rageEventWindow: DEFAULTS.RAGE_EVENT_WINDOW,
      enabled: true
    };

    const settings = { ...defaults, ...config };
    
    DEFAULTS.RAGE_CLICK_THRESHOLD = settings.rageClickThreshold;
    DEFAULTS.RAGE_CLICK_WINDOW = settings.rageClickWindow;
    DEFAULTS.RAGE_EVENT_THRESHOLD = settings.rageEventThreshold;
    DEFAULTS.RAGE_EVENT_WINDOW = settings.rageEventWindow;
  }

  generateRageEventKey(eventType, properties = {}) {
    const propertiesHash = JSON.stringify(properties);
    return `${eventType}:${propertiesHash}`;
  }

  isRageEvent(eventType, properties = {}) {
    const now = Date.now();
    const key = this.generateRageEventKey(eventType, properties);
    const eventTracker = this.rageEventTracker.events.get(key);
    
    if (!eventTracker) return false;

    const isClickEvent = eventType === 'click' || eventType.includes('click');
    const threshold = isClickEvent ? DEFAULTS.RAGE_CLICK_THRESHOLD : DEFAULTS.RAGE_EVENT_THRESHOLD;
    const window = isClickEvent ? DEFAULTS.RAGE_CLICK_WINDOW : DEFAULTS.RAGE_EVENT_WINDOW;

    const recentEvents = eventTracker.timestamps.filter(timestamp => 
      now - timestamp <= window
    );

    return recentEvents.length >= threshold;
  }

  trackRageEvent(eventType, properties = {}) {
    if (eventType === 'navigation') return;
    
    const now = Date.now();
    const key = this.generateRageEventKey(eventType, properties);

    if (!this.rageEventTracker.events.has(key)) {
      this.rageEventTracker.events.set(key, {
        eventType,
        properties,
        timestamps: [],
        count: 0,
        firstSeen: now,
        lastSeen: now
      });
    }

    const tracker = this.rageEventTracker.events.get(key);
    tracker.timestamps.push(now);
    tracker.count++;
    tracker.lastSeen = now;

    const maxWindow = Math.max(DEFAULTS.RAGE_CLICK_WINDOW, DEFAULTS.RAGE_EVENT_WINDOW);
    tracker.timestamps = tracker.timestamps.filter(timestamp => 
      now - timestamp <= maxWindow
    );

    if (this.isRageEvent(eventType, properties)) {
      this.handleRageEvent(key, tracker);
    }

    if (now - this.rageEventTracker.lastCleanup > this.rageEventTracker.cleanupInterval) {
      this.cleanupRageEventTracker();
      this.rageEventTracker.lastCleanup = now;
    }
  }

  handleRageEvent(key, tracker) {
    const now = Date.now();
    const eventType = tracker.eventType;
    const isClickEvent = eventType === 'click' || eventType.includes('click');
    const maxWaitTime = isClickEvent ? DEFAULTS.RAGE_CLICK_WINDOW : DEFAULTS.RAGE_EVENT_WINDOW;

    const existingTimeout = this.rageEventTracker.rageTimeouts.get(eventType);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.rageEventTracker.rageTimeouts.delete(eventType);
    }

    const window = isClickEvent ? DEFAULTS.RAGE_CLICK_WINDOW : DEFAULTS.RAGE_EVENT_WINDOW;
    // const recentEvents = tracker.timestamps.filter(timestamp => 
    //   now - timestamp <= window
    // );
    
    const existing = this.rageEventTracker.pendingRageEvents.get(eventType);
    this.rageEventTracker.pendingRageEvents.set(eventType, {
      ...tracker,
      rageStarted: existing?.rageStarted || now,
      count: tracker.count
    });

    const timeoutId = setTimeout(() => {
      this.sendRageEvent(eventType);
      this.rageEventTracker.rageTimeouts.delete(eventType);
    }, maxWaitTime);

    this.rageEventTracker.rageTimeouts.set(eventType, timeoutId);
  }

  sendRageEvent(eventType) {
    const pendingEvent = this.rageEventTracker.pendingRageEvents.get(eventType);
    if (!pendingEvent) return;

    const now = new Date();
    const currentPath = window.location.pathname;
    const currentUrl = window.location.href;
    const previousPath = document.referrer;

    const rageEventProperties = {
      ...pendingEvent.properties,
      rage_count: pendingEvent.count,
      first_rage_timestamp: toISTTime(pendingEvent.rageStarted),
      last_rage_timestamp: getISTTime(),
      total_events_in_rage: pendingEvent.count,
      rage_duration_ms: now.getTime() - pendingEvent.rageStarted,
      original_event_type: pendingEvent.eventType,
      rage_threshold_met: true,
      rage_window_ms: pendingEvent.eventType.includes('click') ? DEFAULTS.RAGE_CLICK_WINDOW : DEFAULTS.RAGE_EVENT_WINDOW
    };

    const rageEvent = {
      event_id: "evt_" + now.getTime(),
      event_type: `${pendingEvent.eventType}_rage`,
      timestamp: getISTTime(),
      context: {
        page: {
          path: currentPath,
          referrer: previousPath,
          title: document.title,
          url: currentUrl,
        },
      },
      properties: rageEventProperties,
      metadata: {
        client_sent_at: getISTTime(),
        received_at: null,
        processing_time_ms: null,
        library: { name: "dhan-tracker-js", version: DEFAULTS.LIBRARY.version },
        rage_event: true,
      },
    };

    this.rageEventTracker.pendingRageEvents.delete(eventType);
    this.rageEventTracker.rageTimeouts.delete(eventType);

    // Call the callback if it exists
    if (this.onRageEvent) {
      this.onRageEvent(rageEvent);
    }

    return rageEvent;
  }

  cleanupRageEventTracker() {
    const now = Date.now();
    const maxWindow = Math.max(DEFAULTS.RAGE_CLICK_WINDOW, DEFAULTS.RAGE_EVENT_WINDOW) * 2;
    
    for (const [key, tracker] of this.rageEventTracker.events.entries()) {
      if (now - tracker.lastSeen > maxWindow) {
        if (this.rageEventTracker.pendingRageEvents.has(tracker.eventType)) {
          this.sendRageEvent(tracker.eventType);
        }
        this.rageEventTracker.events.delete(key);
      }
    }
    
    // Clean up stale timeouts
    for (const [eventType, timeoutId] of this.rageEventTracker.rageTimeouts.entries()) {
      if (!this.rageEventTracker.pendingRageEvents.has(eventType)) {
        clearTimeout(timeoutId);
        this.rageEventTracker.rageTimeouts.delete(eventType);
      }
    }
  }

  getRageEventInfo(eventType, properties = {}) {
    const key = this.generateRageEventKey(eventType, properties);
    const tracker = this.rageEventTracker.events.get(key);
    
    if (!tracker) return null;

    const isClickEvent = eventType === 'click' || eventType.includes('click');
    const window = isClickEvent ? DEFAULTS.RAGE_CLICK_WINDOW : DEFAULTS.RAGE_EVENT_WINDOW;
    const now = Date.now();
    
    const recentEvents = tracker.timestamps.filter(timestamp => 
      now - timestamp <= window
    );

    return {
      eventType: tracker.eventType,
      properties: tracker.properties,
      totalCount: tracker.count,
      recentCount: recentEvents.length,
      firstSeen: tracker.firstSeen,
      lastSeen: tracker.lastSeen,
      isRage: recentEvents.length >= (isClickEvent ? DEFAULTS.RAGE_CLICK_THRESHOLD : DEFAULTS.RAGE_EVENT_THRESHOLD)
    };
  }

  getRageDetectionStats() {
    const stats = {
      totalTrackedEvents: 0,
      rageEvents: 0,
      eventTypes: new Map()
    };

    for (const [key, tracker] of this.rageEventTracker.events.entries()) {
      stats.totalTrackedEvents += tracker.count;
      
      const rageInfo = this.getRageEventInfo(tracker.eventType, tracker.properties);
      if (rageInfo && rageInfo.isRage) {
        stats.rageEvents++;
      }

      if (!stats.eventTypes.has(tracker.eventType)) {
        stats.eventTypes.set(tracker.eventType, 0);
      }
      stats.eventTypes.set(tracker.eventType, stats.eventTypes.get(tracker.eventType) + tracker.count);
    }

    return {
      ...stats,
      eventTypes: Object.fromEntries(stats.eventTypes),
      activeTrackers: this.rageEventTracker.events.size
    };
  }

  cleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    
    for (const eventType of this.rageEventTracker.pendingRageEvents.keys()) {
      this.sendRageEvent(eventType);
    }
    
    for (const timeoutId of this.rageEventTracker.rageTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    
    if (this.rageEventTracker) {
      this.rageEventTracker.events.clear();
      this.rageEventTracker.pendingRageEvents.clear();
      this.rageEventTracker.rageTimeouts.clear();
    }
  }
}